import React, { useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { colors } from '@/constants/colors';
import PageTitle from '@/components/Admin/PageTitle';
import SectionTitle from '@/components/Admin/SectionTitle';
import { currencyFormat } from '@/utils/currencyFormatter';
import { listMyVendoPayoutsApi } from '@/api/vendo';

/**
 * /wifi-voucher-payout — operator-facing vendo payout history.
 * Reached via the "View all" button on the CardSales widget on
 * /wifi-vouchers. Owner-scoped: only the signed-in operator's own
 * vendo payouts, never voucher-store payouts (those live at /payout).
 */

function relativeTime(iso: string): string {
   const diff = Date.now() - new Date(iso).getTime();
   const sec = Math.floor(diff / 1000);
   if (sec < 5) return 'just now';
   if (sec < 60) return `${sec}s ago`;
   const min = Math.floor(sec / 60);
   if (min < 60) return min === 1 ? 'a minute ago' : `${min}m ago`;
   const hr = Math.floor(min / 60);
   if (hr < 24) return hr === 1 ? 'an hour ago' : `${hr}h ago`;
   const day = Math.floor(hr / 24);
   if (day < 7) return day === 1 ? 'yesterday' : `${day}d ago`;
   return new Date(iso).toLocaleDateString();
}

const WifiVoucherPayout: React.FC = () => {
   const navigate = useNavigate();
   const [page, setPage] = useState(1);
   const [status, setStatus] = useState<string>('all');

   const { data: payouts, isLoading } = useQuery({
      queryKey: ['wifi-voucher-payouts', page, status],
      queryFn: () =>
         listMyVendoPayoutsApi({
            page,
            limit: 20,
            status: status === 'all' ? undefined : status,
         }),
   });

   return (
      <Wrapper>
         <PageTitle
            title="Vendo Voucher Payouts"
            actions={[]}
            inline={
               <BackBtn onClick={() => navigate('/wifi-vouchers')}>
                  ← Back to Vouchers
               </BackBtn>
            }
         />
         <Body>
            <SectionTitle title="Payout History" />
            <FilterRow>
               {(['all', 'processing', 'settled', 'rejected'] as const).map(
                  (s) => (
                     <Chip
                        key={s}
                        $active={status === s}
                        onClick={() => {
                           setStatus(s);
                           setPage(1);
                        }}
                     >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                     </Chip>
                  )
               )}
               <FilterCount>{payouts?.total ?? 0} total</FilterCount>
            </FilterRow>

            <TableScroll>
               <Table>
                  <thead>
                     <tr>
                        <Th>Date</Th>
                        <Th>Payment Method</Th>
                        <Th>Account Name</Th>
                        <Th>Account Number</Th>
                        <Th $right>Amount</Th>
                        <Th $right>Fee</Th>
                        <Th $right>Net</Th>
                        <Th>Status</Th>
                     </tr>
                  </thead>
                  <tbody>
                     {isLoading && (
                        <tr>
                           <Td colSpan={8}>
                              <SkeletonLines lines={3} />
                           </Td>
                        </tr>
                     )}
                     {!isLoading && payouts && payouts.rows.length === 0 && (
                        <tr>
                           <Td colSpan={8}>
                              <Empty>
                                 No payouts yet. Once you request a withdrawal
                                 it will show up here.
                              </Empty>
                           </Td>
                        </tr>
                     )}
                     {payouts?.rows.map((p) => (
                        <tr key={p._id}>
                           <Td>{relativeTime(p.createdAt)}</Td>
                           <Td>{p.payoutMethod}</Td>
                           <Td>{p.payoutAccount}</Td>
                           <Td>{p.payoutAccountNumber}</Td>
                           <Td $right>{currencyFormat(p.amount, false)}</Td>
                           <Td $right>
                              {currencyFormat(p.disbursementFee, false)}
                           </Td>
                           <Td $right>{currencyFormat(p.netAmount, false)}</Td>
                           <Td>
                              <StatusBadge $status={p.status}>
                                 {p.status}
                              </StatusBadge>
                           </Td>
                        </tr>
                     ))}
                  </tbody>
               </Table>
            </TableScroll>

            <Pager>
               <PagerBtn
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
               >
                  ← Prev
               </PagerBtn>
               <PagerInfo>
                  Page {page}
                  {payouts && payouts.total > 0
                     ? ` of ${Math.ceil(payouts.total / 20)}`
                     : ''}
               </PagerInfo>
               <PagerBtn
                  onClick={() => setPage((p) => p + 1)}
                  disabled={
                     !payouts || page >= Math.ceil((payouts.total || 1) / 20)
                  }
               >
                  Next →
               </PagerBtn>
            </Pager>
         </Body>
      </Wrapper>
   );
};

export default WifiVoucherPayout;

/* ─── styled ───────────────────────────────────────────────── */

const Wrapper = styled.div``;

const BackBtn = styled.button`
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY}40;
   border-radius: 8px;
   padding: 6px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;

   &:hover { background: ${colors.PRIMARY}10; }
`;

const Body = styled.div`
   padding: 0 1rem 2rem;
`;

const FilterRow = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;
   margin-bottom: 14px;
   flex-wrap: wrap;
`;

const Chip = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.PRIMARY : colors.WHITE)};
   color: ${({ $active }) => ($active ? colors.WHITE : colors.GRAY3)};
   border: 1px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 16px;
   padding: 4px 12px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   &:hover:not(:disabled) { border-color: ${colors.PRIMARY}; }
`;

const FilterCount = styled.span`
   margin-left: auto;
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const TableScroll = styled.div`
   overflow-x: auto;
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   background: ${colors.WHITE};
`;

const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 12px;
`;

const Th = styled.th<{ $right?: boolean }>`
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   font-weight: 600;
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
   padding: 10px 12px;
   border-bottom: 1px solid ${colors.GRAY13};
   background: ${colors.GRAY14};
`;

const Td = styled.td<{ $right?: boolean }>`
   padding: 10px 12px;
   border-bottom: 1px solid ${colors.GRAY13};
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   color: ${colors.GRAY1};
`;

const StatusBadge = styled.span<{ $status: string }>`
   display: inline-block;
   padding: 2px 8px;
   border-radius: 10px;
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   background: ${({ $status }) => {
      if ($status === 'settled') return 'rgba(34, 197, 94, 0.16)';
      if (
         $status === 'failed' ||
         $status === 'rejected' ||
         $status === 'cancelled'
      )
         return 'rgba(239, 68, 68, 0.16)';
      return 'rgba(234, 179, 8, 0.16)';
   }};
   color: ${({ $status }) => {
      if ($status === 'settled') return '#16a34a';
      if (
         $status === 'failed' ||
         $status === 'rejected' ||
         $status === 'cancelled'
      )
         return '#dc2626';
      return '#ca8a04';
   }};
`;


const Empty = styled.div`
   padding: 28px;
   text-align: center;
   color: ${colors.GRAY5};
   font-size: 12px;
`;

const Pager = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;
   justify-content: center;
   padding: 12px 0;
`;

const PagerBtn = styled.button`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   padding: 5px 12px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PagerInfo = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;
